import React from "react";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import { useComposition } from "../contexts/CompositionContext";
import { useI18n } from "../contexts/I18nContext";
import { findProduct } from "../data/products";
import CustomizableSvg from "./CustomizableSvg";

// Constante miroir de CrossConfigurator (CROSS_PX_PER_CM = 2.5 / 1.5).
const CROSS_PX_PER_CM = 2.5 / 1.5;

// Couleurs néon Signapharma (snapshot autonome — ne pas importer depuis
// CrossConfigurator pour éviter l'import circulaire avec ce composant).
const COLOR_HEX = {
  vert: "#22FF00",
  "bleu-fonce": "#2E5BFF",
  "bleu-clair": "#00E5FF",
  rouge: "#FF1F2D",
  orange: "#FF7A00",
  jaune: "#FFE600",
  blanc: "#FFFFFF",
  magenta: "#FF1FA8",
  // alias supplémentaires utilisés par NeonConfigurator / NEON_PALETTE
  "vert-neon": "#39FF14",
  rose: "#FF3DCC",
  bleu: "#00B8FF",
  violet: "#B026FF",
  "rose-neon": "#ff00c8",
};

const CROSS_SLUGS = new Set(["croix-classique-led", "croix-neon-led", "croix-point-a-point"]);
const BOXLETTER_SLUGS = new Set([
  "lettre-boitier-alu",
  "lettre-retro-eclaire",
  "lettre-inox-3d",
  "enseigne-boitier-chassis",
  "enseigne-interieur-neon-led",
]);
const CADUCEE_SLUG = "caducee";

// Designs et finitions du caducée (miroir de CaduceeConfigurator).
// 11 modèles : PNG premium (1-3, 5) + SVG génériques (4, 6-11).
const CAD_DESIGNS = {
  "1":  { src: "/caducee/caducee-1.png",  aspect: 3530/4545 },
  "2":  { src: "/caducee/caducee-2.png",  aspect: 4036/4548 },
  "3":  { src: "/caducee/caducee-3.png",  aspect: 2813/4547 },
  "4":  { src: "/caducee/caducee-4.svg",  aspect: 902/1111 },
  "5":  { src: "/caducee/caducee-7.png",  aspect: 2938/4594 },
  "6":  { src: "/caducee/caducee-5.svg",  aspect: 990/1111 },
  "7":  { src: "/caducee/caducee-6.svg",  aspect: 803/1111 },
  "8":  { src: "/caducee/caducee-8.svg",  aspect: 902/1122 },
  "9":  { src: "/caducee/caducee-9.svg",  aspect: 891/1122 },
  "10": { src: "/caducee/caducee-10.svg", aspect: 968/1122 },
  "11": { src: "/caducee/caducee-11.svg", aspect: 924/1100 },
};
const CAD_METAL_FILLS = {
  "gold-brillant": "linear-gradient(180deg, #FFF3C9 0%, #FFE08A 8%, #E0A938 24%, #8C5A0A 48%, #5B3700 52%, #8C5A0A 58%, #E0A938 78%, #FFD45A 94%, #FFF3C9 100%)",
  "gold-brosse":   "linear-gradient(180deg, #E9C575 0%, #C99D3D 30%, #8A6112 55%, #B98F2E 80%, #E0BB6A 100%)",
  "cuivre-poli":   "linear-gradient(180deg, #FFE0C2 0%, #F2A877 8%, #C57238 24%, #6F3A12 48%, #3F1F05 52%, #6F3A12 58%, #C57238 78%, #F0A270 94%, #FFE0C2 100%)",
  "cuivre-brosse": "linear-gradient(180deg, #DDA075 0%, #B47148 30%, #774524 55%, #A66B43 80%, #D89B73 100%)",
  "inox-poli":     "linear-gradient(180deg, #ffffff 0%, #f6f6f6 6%, #c8c8c8 22%, #6b6b6b 48%, #3b3b3b 52%, #6b6b6b 58%, #c8c8c8 78%, #f0f0f0 94%, #ffffff 100%)",
  "inox-brosse":   "linear-gradient(180deg, #e4e4e4 0%, #c2c2c2 30%, #8a8a8a 55%, #b0b0b0 80%, #d4d4d4 100%)",
};
const CAD_LED_HEX = { warm3000: "#FFD18A", cool6500: "#EAF6FF", blue: "#00B6FF", green: "#22FF00", red: "#FF1F2D" };
// Même facteur que la preview caducée pour cohérence visuelle (px par cm).
const CAD_PX_PER_CM = 5 / 1.5 / 1.3;

// SVG "+" fallback (cas où on n'a pas de design réel).
const CROSS_PLUS_PATH =
  "M 35 0 L 65 0 L 65 35 L 100 35 L 100 65 L 65 65 L 65 100 L 35 100 L 35 65 L 0 65 L 0 35 L 35 35 Z";

function buildCrossGlow(colorHex, mode) {
  if (!colorHex) return "none";
  // Halo plus discret pour les fantômes (autres catégories) afin de
  // ne pas saturer la scène lorsqu'on change de page. La croix active
  // reste rendue avec son halo complet par le configurateur.
  const lvl = mode === "night" ? 0.45 : 0.25;
  return [
    `drop-shadow(0 0 ${1 * lvl}px #fff)`,
    `drop-shadow(0 0 ${3 * lvl}px ${colorHex})`,
    `drop-shadow(0 0 ${8 * lvl}px ${colorHex})`,
  ].join(" ");
}

function GhostCross({ item, mode }) {
  // Skip the empty sentinel (user chose "Aucun" in the design grid).
  if (item?.designId && String(item.designId).startsWith("_empty_")) return null;
  // Recherche du design via le registre runtime publié par CrossConfigurator.
  const allDesigns = (typeof window !== "undefined" && window.__SIGNAPHARMA_CROSS_DESIGNS__) || [];
  const design = allDesigns.find((d) => d.id === item.designId);
  const colorHex = COLOR_HEX[item.colorId] || "#22FF00";
  const sizePx = (item.sizeCm || 50) * CROSS_PX_PER_CM * (item.scale || 1);
  const glow = buildCrossGlow(colorHex, mode);
  // Pour les croix point-à-point : Gauche/Droite est un MIROIR horizontal,
  // pas une rotation (la grosse patte de fixation bascule de côté).
  const rotationStyle = design?.isDotMatrix
    ? ((item.rotation || 0) === 270 ? "scaleX(-1)" : "none")
    : (item.rotation ? `rotate(${item.rotation}deg)` : "none");

  // 1) Design SVG customizable (Croix V3, Simple, Neon, Ronde V2…)
  if (design?.customizableSvg && design?.svgPath) {
    // Construit les overrides id → couleur depuis customColors stockés sur item.
    // CustomizableSvg supporte `idOverrides` (clé = id SVG, valeur = hex).
    const idOverrides = {};
    if (item.customColors && design.colorLayers) {
      for (const layer of design.colorLayers) {
        const userHex = item.customColors[layer.id];
        if (!userHex) continue;
        if (layer.targetIds) {
          for (const tid of layer.targetIds) idOverrides[tid] = userHex;
        }
      }
    }
    // Sinon tintColor global appliqué via filter saturate+hue-rotate ? trop hasardeux.
    // À défaut, on laisse les couleurs d'origine (vert clair néon #22FF00).
    return (
      <div style={{ width: sizePx, height: sizePx, filter: glow, transform: rotationStyle }}>
        <CustomizableSvg
          src={design.svgPath}
          viewBox={design.viewBox}
          idOverrides={idOverrides}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    );
  }

  // 2) Design PNG (Multi-couleur, Ronde Multi, Flèche Multi preset).
  // On utilise le PNG color (rendu visuel complet du design).
  if (design?.imgColor) {
    return (
      <img
        src={design.imgColor}
        alt=""
        draggable={false}
        style={{
          width: sizePx,
          height: sizePx,
          objectFit: "contain",
          filter: glow,
          transform: rotationStyle,
        }}
      />
    );
  }

  // 3) Fallback SVG "+" plat coloré
  return (
    <svg viewBox="0 0 100 100" width={sizePx} height={sizePx} style={{ filter: glow, transform: rotationStyle }}>
      <path d={CROSS_PLUS_PATH} fill={colorHex} />
    </svg>
  );
}

// ------- Texte boîtier/inox/rétro/néon (rendu haute fidélité) -------
function GhostText({ line, signType, sizeId, customHeightCm, mode }) {
  if (!line?.text) return null;
  const baseSizePx =
    sizeId === "h15" ? 22 :
    sizeId === "h25" ? 34 :
    sizeId === "h60" ? 60 :
    customHeightCm ? Math.max(8, Math.round(customHeightCm * (44 / 40))) :
    44;
  const sizePx = baseSizePx * (line.scale || 1);
  const colorHex = COLOR_HEX[line.colorId] || line.colorHex || "#22FF00";
  const isInox = signType === "inox";
  const isNeon = signType === "neon-custom";

  // Pour le néon : on garde `pre-line` pour qu'un texte tapé en plusieurs
  // lignes (Pharmacie\nde la Gare) s'affiche correctement comme ghost sur
  // les autres pages. Pour les boîtier/inox/retro un \n est peu probable
  // (ils utilisent lines[] séparés) mais on garde le même comportement.
  if (isInox) {
    // Effet métallique simplifié : couleur centrale gris clair + ombre profonde.
    return (
      <span
        style={{
          fontFamily: line.fontFamily || "'Bebas Neue', sans-serif",
          fontWeight: line.fontWeight || 700,
          fontSize: `${sizePx}px`,
          lineHeight: 1.05,
          color: "#d0d0d0",
          textShadow:
            "0 1px 1px rgba(0,0,0,0.7), 0 4px 8px rgba(0,0,0,0.5), 0 10px 18px rgba(0,0,0,0.35)",
          whiteSpace: "pre-line",
          textAlign: "center",
          display: "inline-block",
          letterSpacing: 0.5,
        }}
      >
        {line.text}
      </span>
    );
  }

  // Néon (lettres boîtier rétro / néon perso) : halo coloré réduit pour
  // les fantômes (≈ 40 % de l'intensité d'origine) — la ligne active sur
  // sa propre page conserve son halo complet via son configurateur.
  const intensity = isNeon || mode === "night" ? 0.4 : 0.28;
  return (
    <span
      style={{
        fontFamily: line.fontFamily || "'Bebas Neue', sans-serif",
        fontWeight: line.fontWeight || 700,
        fontSize: `${sizePx}px`,
        lineHeight: 1.05,
        color: colorHex,
        textShadow:
          `0 0 ${2 * intensity}px #fff, ` +
          `0 0 ${6 * intensity}px ${colorHex}, ` +
          `0 0 ${14 * intensity}px ${colorHex}`,
        whiteSpace: "pre-line",
        textAlign: "center",
        display: "inline-block",
        letterSpacing: 0.5,
      }}
    >
      {line.text}
    </span>
  );
}

// ------- Caducée fantôme (silhouette + halo LED) -------
function GhostCaducee({ cfg, mode }) {
  if (!cfg || cfg.visible === false || !cfg.designId) return null;
  const design = CAD_DESIGNS[cfg.designId];
  if (!design) return null;
  const sizeCm = Number(cfg.sizeCm) || 60;
  const scale = Number(cfg.scale) || 1;
  const heightPx = sizeCm * CAD_PX_PER_CM * scale;
  const widthPx = heightPx * design.aspect;
  const background = cfg.finishType === "ral"
    ? (cfg.ralHex || "#888")
    : (CAD_METAL_FILLS[cfg.finishId] || CAD_METAL_FILLS["gold-brillant"]);
  const ledHex = CAD_LED_HEX[cfg.ledId] || CAD_LED_HEX.warm3000;
  // Halo plus discret pour le fantôme (autre page) — 50 % de l'intensité.
  const isNight = mode === "night";
  const filter = cfg.ledOn !== false
    ? (isNight
        ? `drop-shadow(0 0 3px ${ledHex}) drop-shadow(0 0 9px ${ledHex}cc) drop-shadow(0 0 22px ${ledHex}99) drop-shadow(0 0 45px ${ledHex}66)`
        : `drop-shadow(0 0 2px ${ledHex}) drop-shadow(0 0 6px ${ledHex}88) drop-shadow(0 0 14px ${ledHex}55)`)
    : "drop-shadow(0 2px 6px rgba(0,0,0,0.5))";
  return (
    <div
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        WebkitMaskImage: `url('${design.src}')`,
        maskImage: `url('${design.src}')`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        background,
        filter,
      }}
    />
  );
}

// ------- Composant principal -------
export default function CompositionOverlay({ currentSlug, mode = "day" }) {
  const { configs, setPreviousSlug } = useComposition();
  const { lang } = useI18n();
  const navigate = useNavigate();

  const ghosts = [];
  for (const [slug, cfg] of Object.entries(configs || {})) {
    if (!cfg || slug === currentSlug) continue;
    if (slug === CADUCEE_SLUG) {
      // Le caducée est un objet unique (pas une liste d'items). Position via posX/posY.
      if (cfg.visible === false || !cfg.designId) continue;
      ghosts.push({
        kind: "caducee",
        slug,
        cfg,
        pos: { x: Number(cfg.posX) || 50, y: Number(cfg.posY) || 50 },
      });
      continue;
    }
    if (CROSS_SLUGS.has(slug) && Array.isArray(cfg.items)) {
      for (const item of cfg.items) {
        if (!item?.pos) continue;
        ghosts.push({ kind: "cross", slug, item });
      }
    } else if (BOXLETTER_SLUGS.has(slug) && Array.isArray(cfg.lines)) {
      for (const line of cfg.lines) {
        if (!line?.pos || !line?.text) continue;
        ghosts.push({
          kind: "text",
          slug,
          line,
          signType: cfg.signType,
          sizeId: cfg.sizeId,
          customHeightCm: cfg.customHeightCm,
        });
      }
    }
  }

  if (ghosts.length === 0) return null;

  const openSource = (slug) => {
    if (currentSlug) setPreviousSlug(currentSlug);
    const product = findProduct(slug);
    const cat = product?.category || "croix-pharmacie";
    navigate(`/produits/${cat}/${slug}`);
  };

  return (
    <div
      data-testid="composition-overlay"
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 5 }}
    >
      {ghosts.map((g, idx) => {
        const pos = g.kind === "cross"
          ? g.item.pos
          : g.kind === "caducee"
            ? g.pos
            : g.line.pos;
        const product = findProduct(g.slug);
        const productName = product?.name?.[lang] || product?.name?.fr || g.slug;
        return (
          <div
            key={`ghost-${g.slug}-${idx}`}
            data-testid={`ghost-${g.slug}-${idx}`}
            className="absolute group pointer-events-auto"
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: "translate(-50%, -50%)",
              opacity: 0.75,
            }}
            onClick={(e) => {
              e.stopPropagation();
              openSource(g.slug);
            }}
            title={`${productName} — ${lang === "fr" ? "cliquer pour éditer" : "click to edit"}`}
          >
            <div className="relative">
              {/* Outline dashed apparait au hover pour préciser l'élément éditable */}
              <div className="absolute -inset-3 border border-dashed border-[#39FF14]/0 group-hover:border-[#39FF14]/70 pointer-events-none transition-colors" />
              <div
                className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/85 border border-[#39FF14]/60 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
                style={{ zIndex: 10 }}
              >
                <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-[#39FF14]">
                  <Pencil size={10} />
                  {productName}
                </div>
              </div>
              <div className="flex items-center justify-center">
                {g.kind === "cross" ? (
                  <GhostCross item={g.item} mode={mode} />
                ) : g.kind === "caducee" ? (
                  <GhostCaducee cfg={g.cfg} mode={mode} />
                ) : (
                  <GhostText
                    line={g.line}
                    signType={g.signType}
                    sizeId={g.sizeId}
                    customHeightCm={g.customHeightCm}
                    mode={mode}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
